import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Skeleton, Chip, Select, MenuItem, ToggleButtonGroup, ToggleButton, IconButton, LinearProgress, Tooltip } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import CrisisAlertOutlinedIcon from '@mui/icons-material/CrisisAlertOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { AppDispatch, RootState } from '../../store/store';
import { getInsights } from '../../rtk-apis/dashboard/getInsights';
import StudyTypeChart from '../../components/StudyTypeChart';
import AlertSettingsDialog from './AlertSettingsDialog';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* ════════════════════════════════════════════════════════════════════════
   Clinic Admin dashboard — whole-clinic operational insights.
   Sibling of CardiologistDashboard: MUI shell + KPI cards + Highcharts.
   Data from GET /v2/dashboard/insights (role-aware — Admin payload).
   ════════════════════════════════════════════════════════════════════════ */

const fmtShortDate = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][+m[2] - 1];
  return `${+m[3]} ${mon}`;
};

const daysOverdue = (createdOn?: string) => {
  if (!createdOn) return 0;
  const t = new Date(createdOn).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

const chartBase: Highcharts.Options = {
  credits: { enabled: false },
  title: { text: undefined },
  chart: { backgroundColor: 'transparent', style: { fontFamily: "'Hanken Grotesk', sans-serif" } },
  legend: { itemStyle: { color: T.ink2, fontWeight: '600' }, itemHoverStyle: { color: T.ink } },
};

const CURRENT_YEAR = new Date().getFullYear();
const YEARS: number[] = [];
for (let y = 2021; y <= CURRENT_YEAR; y++) YEARS.push(y);

const AdminDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { result, loading } = useSelector((s: RootState) => s.getInsightsResponse);
  const data = result?.data;

  const [studyTypeYear, setStudyTypeYear] = useState<number>(CURRENT_YEAR);
  const [referrerRange, setReferrerRange] = useState<'day' | 'month' | 'year'>('month');
  const [overdueSort, setOverdueSort] = useState<'longest' | 'recent'>('longest');
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);

  useEffect(() => {
    dispatch(getInsights({ studyTypeYear, referrerRange, overdueSort }));
  }, [dispatch, studyTypeYear, referrerRange, overdueSort]);

  // Skeletons only on the first load. Control-driven refetches (year select,
  // day/month toggle, overdue sort) keep the last data on screen and show a
  // thin progress bar on the affected panel instead of blanking every chart
  // (Nik: "it is refreshing all — should refresh the single box").
  const initialLoading = loading && !data;
  const refreshing = loading && !!data;

  const k = data?.kpis;
  const statusB = data?.statusBreakdown || { Draft: 0, Preliminary: 0, Finalized: 0 };
  const trend = data?.trend || [];
  const studyTypeByMonth = data?.studyTypeByMonth;
  const referrerBreakdown = data?.referrerBreakdown || [];
  const doctorPerformance = data?.doctorPerformance || [];
  const usersByRole = data?.usersByRole || [];
  const weekdayVolume = data?.weekdayVolume || [];
  const overdueQueue = data?.overdueQueue || [];

  // ── studies-this-month delta vs the all-time monthly average ──────────
  // (Nik: mid-month "vs last month" misleads — compare to the clinic's
  // monthly average instead; current partial month excluded from the avg.)
  const delta = useMemo(() => {
    const cur = k?.studiesThisMonth ?? 0;
    const avg = k?.studiesMonthlyAvg;
    if (!avg) return { label: '—', color: T.ink3 };
    const pct = Math.round(((cur - avg) / avg) * 100);
    if (pct === 0) return { label: '= avg', color: T.ink3 };
    return pct > 0
      ? { label: `▲ ${pct}%`, color: T.green }
      : { label: `▼ ${Math.abs(pct)}%`, color: T.red };
  }, [k?.studiesThisMonth, k?.studiesMonthlyAvg]);

  // ── chart options ──────────────────────────────────────────────────────
  const trendOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'areaspline', height: 280 },
    xAxis: {
      categories: trend.map((d: any) => fmtShortDate(d.date)),
      labels: { style: { color: T.ink3, fontSize: '10px' }, step: 4 },
      lineColor: T.line,
      tickColor: T.line,
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: 'rgba(255,255,255,0.05)',
      labels: { style: { color: T.ink3 } },
      allowDecimals: false,
    },
    tooltip: { shared: true, backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink } },
    plotOptions: { areaspline: { fillOpacity: 0.18, lineWidth: 2, marker: { enabled: false } } },
    series: [
      { type: 'areaspline', name: 'Created', data: trend.map((d: any) => d.created), color: T.sky },
      { type: 'areaspline', name: 'Finalized', data: trend.map((d: any) => d.finalized), color: T.teal },
    ],
  }), [trend]);

  const statusOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'pie', height: 280 },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b> ({point.percentage:.0f}%)' },
    plotOptions: { pie: { innerSize: '62%', borderWidth: 0, dataLabels: { enabled: true, style: { color: T.ink2, textOutline: 'none' }, format: '{point.name}: {point.y}' } } },
    series: [{
      type: 'pie',
      name: 'Reports',
      data: [
        { name: 'Draft', y: statusB.Draft, color: T.amber },
        { name: 'Preliminary', y: statusB.Preliminary, color: T.sky },
        { name: 'Finalized', y: statusB.Finalized, color: T.green },
      ],
    }],
  }), [statusB]);

  const referrerOptions = useMemo<Highcharts.Options>(() => {
    const top = referrerBreakdown.slice(0, 10);
    return {
      ...chartBase,
      chart: { ...chartBase.chart, type: 'bar', height: 280 },
      xAxis: { categories: top.map((r: any) => r.name), labels: { style: { color: T.ink2 } }, lineColor: T.line },
      yAxis: { title: { text: undefined }, gridLineColor: 'rgba(255,255,255,0.05)', labels: { style: { color: T.ink3 } }, allowDecimals: false },
      legend: { enabled: false },
      tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b> referrals' },
      plotOptions: { bar: { borderRadius: 4, color: T.sky, borderWidth: 0 } },
      series: [{ type: 'bar', name: 'Referrals', data: top.map((r: any) => r.count) }],
    };
  }, [referrerBreakdown]);

  const doctorOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'bar', height: 280 },
    xAxis: { categories: doctorPerformance.map((d: any) => d.name), labels: { style: { color: T.ink2 } }, lineColor: T.line },
    yAxis: { title: { text: undefined }, gridLineColor: 'rgba(255,255,255,0.05)', labels: { style: { color: T.ink3 } }, allowDecimals: false },
    legend: { enabled: false },
    tooltip: {
      backgroundColor: '#02080f',
      borderColor: T.line,
      style: { color: T.ink },
      pointFormat: '<b>{point.y}</b> finalized · avg TAT {point.custom.tatLabel}',
    },
    plotOptions: { bar: { borderRadius: 4, color: T.teal, borderWidth: 0 } },
    series: [{
      type: 'bar',
      name: 'Finalized',
      data: doctorPerformance.map((d: any) => ({
        y: d.finalized,
        custom: { tatLabel: d.avgTatHours != null ? `${d.avgTatHours}h` : '—' },
      })),
    }],
  }), [doctorPerformance]);

  const weekdayOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'column', height: 280 },
    xAxis: { categories: weekdayVolume.map((d: any) => d.day), labels: { style: { color: T.ink2 } }, lineColor: T.line },
    yAxis: { title: { text: undefined }, gridLineColor: 'rgba(255,255,255,0.05)', labels: { style: { color: T.ink3 } }, allowDecimals: false },
    legend: { enabled: false },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b> studies' },
    plotOptions: { column: { borderRadius: 3, color: T.sky, borderWidth: 0 } },
    series: [{ type: 'column', name: 'Studies', data: weekdayVolume.map((d: any) => d.count) }],
  }), [weekdayVolume]);

  const roleColors = [T.teal, T.sky, T.violet, T.amber, T.green, T.red];
  const roleOptions = useMemo<Highcharts.Options>(() => ({
    ...chartBase,
    chart: { ...chartBase.chart, type: 'pie', height: 280 },
    tooltip: { backgroundColor: '#02080f', borderColor: T.line, style: { color: T.ink }, pointFormat: '<b>{point.y}</b> users' },
    plotOptions: { pie: { innerSize: '62%', borderWidth: 0, dataLabels: { enabled: true, style: { color: T.ink2, textOutline: 'none' }, format: '{point.name}: {point.y}' } } },
    series: [{
      type: 'pie',
      name: 'Users',
      data: usersByRole.map((u: any, i: number) => ({ name: u.role, y: u.count, color: roleColors[i % roleColors.length] })),
    }],
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [usersByRole]);

  const hasStudyType = !!studyTypeByMonth?.categories?.length;
  const newPatients = k?.newPatientsThisMonth;

  return (
    <Box sx={{ px: '30px', pb: '30px', pt: '18px', color: T.ink, fontFamily: "'Hanken Grotesk', sans-serif", overflowY: 'auto', height: 'calc(100vh - 64px)' }}>
      {/* header */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.24em', textTransform: 'uppercase', color: T.teal, opacity: 0.85 }}>
          Clinic Overview
        </Typography>
        <Typography component="h1" sx={{ fontSize: 30, fontWeight: 700, lineHeight: 1.05, background: 'linear-gradient(92deg,#fff,#bfe9f5 55%,#7fe3d4)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Dashboard
        </Typography>
      </Box>

      {/* KPI cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)', xl: 'repeat(4,1fr)' }, gap: '14px', mb: '18px' }}>
        <Kpi
          loading={initialLoading}
          icon={<MonitorHeartOutlinedIcon />}
          accent={T.teal}
          label="Studies this month"
          value={k?.studiesThisMonth}
          sub={k?.studiesMonthlyAvg != null ? `compared to monthly average (${k.studiesMonthlyAvg}/mo)` : undefined}
          delta={
            <Chip
              label={delta.label}
              size="small"
              sx={{ height: 20, fontWeight: 700, fontSize: 11, borderRadius: '999px', color: delta.color, background: `${delta.color}1f`, border: `1px solid ${delta.color}66` }}
            />
          }
        />
        <Kpi loading={initialLoading} icon={<PendingActionsOutlinedIcon />} accent={T.amber} label="Pending reports" value={k?.pendingReports} highlight={(k?.pendingReports || 0) > 0} />
        <Kpi loading={initialLoading} icon={<CrisisAlertOutlinedIcon />} accent={T.red} label="Overdue reports" value={k?.overdueReports} highlight={(k?.overdueReports || 0) > 0} />
        <Kpi loading={initialLoading} icon={<AssignmentTurnedInOutlinedIcon />} accent={T.green} label="Finalized this month" value={k?.finalizedThisMonth} />
        <Kpi loading={initialLoading} icon={<TimerOutlinedIcon />} accent={T.sky} label="Avg turnaround" value={k?.avgTatHours != null ? k.avgTatHours : '—'} unit={k?.avgTatHours != null ? 'h' : ''} sub="study created → finalized, last 30 days" />
        <Kpi loading={initialLoading} icon={<SendOutlinedIcon />} accent={T.violet} label="Distribution success" value={k?.distribution?.successRate != null ? k.distribution.successRate : '—'} unit={k?.distribution?.successRate != null ? '%' : ''} sub={k?.distribution?.total ? `${k.distribution.delivered}/${k.distribution.total} delivered` : 'no sends'} />
        <Kpi loading={initialLoading} icon={<GroupsOutlinedIcon />} accent={T.teal} label="Active users" value={k?.activeUsers} />
        <Kpi loading={initialLoading} icon={<PersonAddAltOutlinedIcon />} accent={T.sky} label={newPatients != null ? 'New patients (mo)' : 'Patients'} value={newPatients != null ? newPatients : k?.totalPatients} />
      </Box>

      {/* charts row 1 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: '14px', mb: '14px' }}>
        <Panel title="Reports — last 30 days" subtitle="Created vs finalized, whole clinic" loading={initialLoading}>
          <HighchartsReact highcharts={Highcharts} options={trendOptions} />
        </Panel>
        <Panel title="Report status" subtitle="All reports in your clinic" loading={initialLoading}>
          <HighchartsReact highcharts={Highcharts} options={statusOptions} />
        </Panel>
      </Box>

      {/* charts row 2 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '14px', mb: '14px' }}>
        <Panel
          title="Echo vs Stress Echo"
          subtitle={`Studies per month, ${studyTypeYear}`}
          loading={initialLoading}
          refreshing={refreshing}
          empty={!hasStudyType}
          action={
            <Select
              size="small"
              value={studyTypeYear}
              onChange={e => setStudyTypeYear(Number(e.target.value))}
              sx={{ fontSize: 13, color: T.ink2, '.MuiSelect-select': { py: '4px' } }}
            >
              {YEARS.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          }
        >
          <StudyTypeChart categories={studyTypeByMonth?.categories || []} series={studyTypeByMonth?.series || []} />
        </Panel>
        <Panel
          title="Referring doctors"
          subtitle="Top referrers by study count"
          loading={initialLoading}
          refreshing={refreshing}
          empty={referrerBreakdown.length === 0}
          action={
            <ToggleButtonGroup
              size="small"
              exclusive
              value={referrerRange}
              onChange={(_e, v) => v && setReferrerRange(v)}
              sx={{ '& .MuiToggleButton-root': { py: '2px', px: '10px', fontSize: 12, color: T.ink3, borderColor: T.line, '&.Mui-selected': { color: T.teal, background: `${T.teal}1f` } } }}
            >
              <ToggleButton value="day">Day</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
              <ToggleButton value="year">Year</ToggleButton>
            </ToggleButtonGroup>
          }
        >
          <HighchartsReact highcharts={Highcharts} options={referrerOptions} />
        </Panel>
      </Box>

      {/* charts row 3 */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '14px', mb: '14px' }}>
        <Panel title="Doctor performance" subtitle="Reports finalized per doctor, last 90 days" loading={initialLoading} empty={doctorPerformance.length === 0}>
          <HighchartsReact highcharts={Highcharts} options={doctorOptions} />
        </Panel>
        <Panel title="Busiest days" subtitle="Studies by weekday, last 90 days" loading={initialLoading} empty={weekdayVolume.length === 0}>
          <HighchartsReact highcharts={Highcharts} options={weekdayOptions} />
        </Panel>
      </Box>

      {/* bottom row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: '14px' }}>
        <Panel
          title="Overdue reports"
          subtitle={overdueSort === 'longest' ? 'Longest-waiting unfinalized reports' : 'Most recent unfinalized reports'}
          loading={initialLoading}
          refreshing={refreshing}
          empty={!loading && overdueQueue.length === 0}
          emptyText="Nothing overdue — the clinic is on time."
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={overdueSort}
                onChange={(_e, v) => v && setOverdueSort(v)}
                sx={{ '& .MuiToggleButton-root': { py: '2px', px: '10px', fontSize: 12, color: T.ink3, borderColor: T.line, '&.Mui-selected': { color: T.teal, background: `${T.teal}1f` } } }}
              >
                <ToggleButton value="longest">Longest overdue</ToggleButton>
                <ToggleButton value="recent">Most recent</ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Alert settings">
                <IconButton size="small" onClick={() => setAlertSettingsOpen(true)} sx={{ color: T.ink3 }}>
                  <SettingsOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          }
        >
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {overdueQueue.map((r: any) => {
              const days = daysOverdue(r.createdOn);
              return (
                <Box
                  key={r._id}
                  onClick={() => navigate(`/patientReports/${r._id}`)}
                  sx={{
                    display: 'grid', gridTemplateColumns: '1fr 130px 110px 70px 24px', alignItems: 'center', gap: '12px',
                    px: '12px', py: '11px', borderBottom: `1px solid ${T.line}`, cursor: 'pointer', borderRadius: '8px',
                    '&:hover': { background: T.surface2 },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.reportName || r.name || r.patientId || r.uhid || 'Report'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: T.ink3 }}>{r.reportType || '—'} · UHID {r.uhid || '—'}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: T.ink2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.reportAssignedTo || '—'}</Typography>
                  <Chip
                    label={`${days}d overdue`}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11, borderRadius: '999px', color: T.red, background: `${T.red}1f`, border: `1px solid ${T.red}66` }}
                  />
                  <Box>
                    {(r.reminderCount || 0) > 0 && (
                      <Chip
                        label={`${r.reminderCount}×`}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, borderRadius: '999px', color: T.amber, background: `${T.amber}1f`, border: `1px solid ${T.amber}66` }}
                      />
                    )}
                  </Box>
                  <ChevronRightIcon sx={{ fontSize: 18, color: T.ink3 }} />
                </Box>
              );
            })}
          </Box>
        </Panel>
        <Panel title="Users per role" subtitle="Active accounts in your clinic" loading={initialLoading} empty={usersByRole.length === 0}>
          <HighchartsReact highcharts={Highcharts} options={roleOptions} />
        </Panel>
      </Box>

      <AlertSettingsDialog
        open={alertSettingsOpen}
        onClose={() => setAlertSettingsOpen(false)}
        onSaved={() => dispatch(getInsights({ studyTypeYear, referrerRange, overdueSort }))}
      />
    </Box>
  );
};

// ── KPI card ─────────────────────────────────────────────────────────────
const Kpi: React.FC<{ icon: React.ReactNode; accent: string; label: string; value: any; unit?: string; sub?: string; highlight?: boolean; loading?: boolean; delta?: React.ReactNode }> = ({ icon, accent, label, value, unit, sub, highlight, loading, delta }) => (
  <Box sx={{ minWidth: 0, position: 'relative', borderRadius: '14px', background: 'linear-gradient(180deg, rgba(20,36,57,0.6), rgba(14,28,46,0.55))', border: `1px solid ${highlight ? `${accent}88` : T.line}`, p: '14px 16px', overflow: 'hidden', minHeight: 96 }}>
    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent }} />
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: accent, mb: '6px' }}>
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.ink3 }}>{label}</Typography>
      <Box sx={{ display: 'flex', '& svg': { fontSize: 20 } }}>{icon}</Box>
    </Box>
    {loading ? (
      <Skeleton variant="text" width={60} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
    ) : (
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        <Typography sx={{ fontFamily: "'Spectral', serif", fontSize: 30, fontWeight: 600, lineHeight: 1, color: T.ink, fontVariantNumeric: 'tabular-nums' }}>
          {value ?? 0}
          {unit && <Box component="span" sx={{ fontSize: 14, color: T.ink3, ml: '3px', fontFamily: "'Hanken Grotesk',sans-serif" }}>{unit}</Box>}
        </Typography>
        {delta}
      </Box>
    )}
    {sub && !loading && <Typography sx={{ fontSize: 11.5, color: T.ink3, mt: '4px' }}>{sub}</Typography>}
  </Box>
);

// ── chart / widget panel ───────────────────────────────────────────────────
const Panel: React.FC<{ title: string; subtitle?: string; loading?: boolean; refreshing?: boolean; empty?: boolean; emptyText?: string; action?: React.ReactNode; children: React.ReactNode }> = ({ title, subtitle, loading, refreshing, empty, emptyText, action, children }) => (
  <Box sx={{ minWidth: 0, overflow: 'hidden', borderRadius: '16px', background: 'rgba(14,28,46,0.55)', border: `1px solid ${T.line}`, p: '16px 18px', position: 'relative' }}>
    {refreshing && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}
    <Box sx={{ mb: '8px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
      <Box>
        <Typography sx={{ fontFamily: "'Spectral', serif", fontSize: 16, fontWeight: 600, color: T.ink }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: 12, color: T.ink3 }}>{subtitle}</Typography>}
      </Box>
      {action}
    </Box>
    {loading ? (
      <Skeleton variant="rounded" height={240} sx={{ bgcolor: 'rgba(255,255,255,0.05)' }} />
    ) : empty ? (
      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.ink3, fontSize: 13 }}>
        {emptyText || 'No data yet.'}
      </Box>
    ) : (
      children
    )}
  </Box>
);

export default AdminDashboard;
